import { uiText, useUiLanguage } from '@/i18n/interface';

import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormData } from '../../utils/formValidation';

interface PersonalInfoFieldsProps {
  formData: FormData;
  updateFormData: (field: string, value: string) => void;
}

const PersonalInfoFields: React.FC<PersonalInfoFieldsProps> = ({ formData, updateFormData }) => {
  useUiLanguage();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="firstName">{uiText("First Name *")}</Label>
        <Input
          id="firstName"
          value={formData.firstName}
          onChange={(e) => updateFormData('firstName', e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="lastName">{uiText("Last Name *")}</Label>
        <Input
          id="lastName"
          value={formData.lastName}
          onChange={(e) => updateFormData('lastName', e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="dateOfBirth">{uiText("Date of Birth *")}</Label>
        <Input
          id="dateOfBirth"
          type="date"
          value={formData.dateOfBirth}
          onChange={(e) => updateFormData('dateOfBirth', e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="email">{uiText("Email *")}</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => updateFormData('email', e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="phone">{uiText("Phone Number *")}</Label>
        <Input
          id="phone"
          value={formData.phone}
          onChange={(e) => updateFormData('phone', e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="address">{uiText("Address *")}</Label>
        <Input
          id="address"
          value={formData.address}
          onChange={(e) => updateFormData('address', e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="city">{uiText("City")}</Label>
        <Input
          id="city"
          value={formData.city}
          onChange={(e) => updateFormData('city', e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="postalCode">{uiText("Postal Code")}</Label>
        <Input
          id="postalCode"
          value={formData.postalCode}
          onChange={(e) => updateFormData('postalCode', e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="country">{uiText("Country *")}</Label>
        <Select value={formData.country} onValueChange={(value) => updateFormData('country', value)}>
          <SelectTrigger>
            <SelectValue placeholder={uiText("Select a country")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Canada">{uiText("Canada")}</SelectItem>
            <SelectItem value="United States">{uiText("United States")}</SelectItem>
            <SelectItem value="United Kingdom">{uiText("United Kingdom")}</SelectItem>
            <SelectItem value="Germany">{uiText("Germany")}</SelectItem>
            <SelectItem value="France">{uiText("France")}</SelectItem>
            <SelectItem value="Netherlands">{uiText("Netherlands")}</SelectItem>
            <SelectItem value="Greece">{uiText("Greece")}</SelectItem>
            <SelectItem value="Spain">{uiText("Spain")}</SelectItem>
            <SelectItem value="Portugal">{uiText("Portugal")}</SelectItem>
            <SelectItem value="Italy">{uiText("Italy")}</SelectItem>
            <SelectItem value="Australia">{uiText("Australia")}</SelectItem>
            <SelectItem value="Japan">{uiText("Japan")}</SelectItem>
            <SelectItem value="Brazil">{uiText("Brazil")}</SelectItem>
            <SelectItem value="Mexico">{uiText("Mexico")}</SelectItem>
            <SelectItem value="Russia">{uiText("Russia")}</SelectItem>
            <SelectItem value="China">{uiText("China")}</SelectItem>
            <SelectItem value="India">{uiText("India")}</SelectItem>
            <SelectItem value="South Korea">{uiText("South Korea")}</SelectItem>
            <SelectItem value="Turkey">{uiText("Turkey")}</SelectItem>
            <SelectItem value="Sweden">{uiText("Sweden")}</SelectItem>
            <SelectItem value="Norway">{uiText("Norway")}</SelectItem>
            <SelectItem value="Denmark">{uiText("Denmark")}</SelectItem>
            <SelectItem value="Belgium">{uiText("Belgium")}</SelectItem>
            <SelectItem value="Switzerland">{uiText("Switzerland")}</SelectItem>
            <SelectItem value="Austria">{uiText("Austria")}</SelectItem>
            <SelectItem value="Czech Republic">{uiText("Czech Republic")}</SelectItem>
            <SelectItem value="Poland">{uiText("Poland")}</SelectItem>
            <SelectItem value="Hungary">{uiText("Hungary")}</SelectItem>
            <SelectItem value="Romania">{uiText("Romania")}</SelectItem>
            <SelectItem value="Bulgaria">{uiText("Bulgaria")}</SelectItem>
            <SelectItem value="Croatia">{uiText("Croatia")}</SelectItem>
            <SelectItem value="Argentina">{uiText("Argentina")}</SelectItem>
            <SelectItem value="Chile">{uiText("Chile")}</SelectItem>
            <SelectItem value="Colombia">{uiText("Colombia")}</SelectItem>
            <SelectItem value="Venezuela">{uiText("Venezuela")}</SelectItem>
            <SelectItem value="Peru">{uiText("Peru")}</SelectItem>
            <SelectItem value="South Africa">{uiText("South Africa")}</SelectItem>
            <SelectItem value="Egypt">{uiText("Egypt")}</SelectItem>
            <SelectItem value="Morocco">{uiText("Morocco")}</SelectItem>
            <SelectItem value="Israel">{uiText("Israel")}</SelectItem>
            <SelectItem value="UAE">{uiText("UAE")}</SelectItem>
            <SelectItem value="Saudi Arabia">{uiText("Saudi Arabia")}</SelectItem>
            <SelectItem value="Thailand">{uiText("Thailand")}</SelectItem>
            <SelectItem value="Philippines">{uiText("Philippines")}</SelectItem>
            <SelectItem value="Vietnam">{uiText("Vietnam")}</SelectItem>
            <SelectItem value="Malaysia">{uiText("Malaysia")}</SelectItem>
            <SelectItem value="Indonesia">{uiText("Indonesia")}</SelectItem>
            <SelectItem value="Singapore">{uiText("Singapore")}</SelectItem>
            <SelectItem value="New Zealand">{uiText("New Zealand")}</SelectItem>
            <SelectItem value="Ireland">{uiText("Ireland")}</SelectItem>
            <SelectItem value="Finland">{uiText("Finland")}</SelectItem>
            <SelectItem value="Other">{uiText("Other")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {formData.country === 'Other' && (
        <div>
          <Label htmlFor="customCountry">{uiText("Please specify your country *")}</Label>
          <Input
            id="customCountry"
            value={formData.customCountry}
            onChange={(e) => updateFormData('customCountry', e.target.value)}
            placeholder={uiText("Enter your country")}
            required
          />
        </div>
      )}
    </div>
  );
};

export default PersonalInfoFields;
